import { createContext, useMemo, useContext, useRef, useState } from "react";
import { colors, shapes, levers, categories, patterns } from "./data";
import { mulberry32, cyrb128 } from "./random";
import * as yellows from "../fragments.codegen/Yellow";
import * as reds from "../fragments.codegen/Red";
import * as purples from "../fragments.codegen/Purple";
import * as blues from "../fragments.codegen/Blue";
import * as teals from "../fragments.codegen/Teal";

const options = (fragments, str) =>
  Object.entries(fragments)
    .filter(([k]) => k.startsWith(str.toLowerCase().replace(/\W/gi, "")))
    .map(([, v]) => v);

const Context = createContext();

export const ArtContext = (props) => {
  // On a phone, open at the 320 breakpoint rather than the desktop default.
  const [width, setWidth] = useState(() =>
    typeof window !== "undefined" && window.innerWidth < 672 ? 320 : 1024
  );
  const bp = width < 672 ? 320 : width < 1056 ? 672 : width < 1312 ? 1056 : width < 1584 ? 1312 : 1584;
  const height = width < 672 ? width / (320 / 240) : width < 1056 ? width / (672 / 336) : 480;
  const bpHeight = width < 672 ? 240 : width < 1056 ? 336 : 480;
  const scale = bp < 672 ? 0.5 : bp < 1056 ? 0.75 : 1;
  const [inputs, setInputs] = useState({
    id: "",
    lever: levers[0],
    shape: shapes[0],
    category: categories[0],
  });
  const ref = useRef();

  const _setInputs = (update) => setInputs((prev) => ({ ...prev, ...update }));

  const specs = useMemo(() => {
    const seed = cyrb128(inputs.id)[0];
    const rnd = mulberry32(seed);
    const one = (arr) => arr[Math.floor(rnd() * arr.length)];
    return {
      seed,
      p: inputs.lever,
      color: colors[Math.floor(rnd() * colors.length)],
      pattern: patterns[Math.floor(rnd() * patterns.length)],
      yellow: one(options(yellows, inputs.lever)),
      purple: one(options(purples, inputs.lever)),
      red: one(options(reds, inputs.category)),
      teal: one(options(teals, inputs.shape)),
      blue: one(options(blues, inputs.category)),
    };
  }, [inputs]);

  return (
    <Context.Provider
      value={{ specs, ref, inputs, setInputs: _setInputs, width, setWidth, bp, height, bpHeight, scale }}
    >
      {props.children}
    </Context.Provider>
  );
};

export const useArtSpecs = () => {
  return useContext(Context);
};
